import Phaser from 'phaser';
import { GAME_CONFIG, getRestoredGroundKey, getBiomeName, getBiomeParticleColors, getBiomeDecorKeys } from '../config/game';
import { tileAssetKey, PlaceableTile, isSeedTile, isWaveTile, SACRED_SPRING, SEED_TILE, WAVE_TILE } from '../config/tiles';
import { getDifficultyTier } from '../config/difficulty';
import { ShapeId } from '../config/shapes';
import { Grid } from '../systems/Grid';
import { TileQueue } from '../systems/TileQueue';
import { Scoring } from '../systems/Scoring';
import { saveGame, clearSave, setHighestLevel, SaveData } from '../systems/SaveState';
import { saveDailyGame, clearDailySave, saveDailyBestScore, getDailyBestScore, DailySaveData } from '../systems/DailySave';
import { generateDailyPuzzle, createDailyRng, buildDailyCells } from '../systems/DailyChallenge';
import { DroughtGauge } from '../systems/DroughtGauge';
import { Streaks } from '../systems/Streaks';
import { getCurrentTier, HARMONY_CONFIG, PATIENCE_CONFIG, RHYTHM_CONFIG } from '../config/streaks';
import { getSoundSystem } from '../systems/SoundSystem';
import { getMusicPlayer } from '../systems/MusicPlayer';
import { BackHandler, pushSceneState } from '../systems/BackButton';

export class GameScene extends Phaser.Scene implements BackHandler {
  private grid!: Grid;
  private tileQueue!: TileQueue;
  private scoring!: Scoring;
  private droughtGauge!: DroughtGauge;
  private streaks!: Streaks;
  private lavaFill!: Phaser.GameObjects.Graphics;
  private volcanoIcon!: Phaser.GameObjects.Image;
  private volcanoGlow!: Phaser.GameObjects.Graphics;
  private glowTween: Phaser.Tweens.Tween | null = null;
  private cellSprites: Phaser.GameObjects.Image[][] = [];
  private tileSprites: (Phaser.GameObjects.Image | null)[][] = [];
  private cellRestored: boolean[][] = [];
  private currentPreview!: Phaser.GameObjects.Image;
  private nextPreview!: Phaser.GameObjects.Image;
  private scoreText!: Phaser.GameObjects.Text;
  private discardZone!: Phaser.GameObjects.Container;
  private gridContainer!: Phaser.GameObjects.Container;
  private streakContainers: Map<string, Phaser.GameObjects.Container> = new Map();
  private streakTexts: Map<string, Phaser.GameObjects.Text> = new Map();
  private streakGlows: Map<string, Phaser.GameObjects.Graphics> = new Map();
  private multiplierText: Phaser.GameObjects.Text | null = null;
  private luminaContainer: Phaser.GameObjects.Container | null = null;
  private luminaGlow: Phaser.GameObjects.Graphics | null = null;
  private cellSize = 0;
  private gridOriginX = 0;
  private gridOriginY = 0;
  private isGameOver = false;
  private level = 1;
  private replay = false;
  private timeAttack = false;
  private daily = false;
  private dailyDate = '';
  private timerStarted = false;
  private elapsedMs = 0;
  private unplaceableTween: Phaser.Tweens.Tween | null = null;

  constructor() {
    super('GameScene');
  }

  handleBack(): void {}

  init(data: { level?: number; replay?: boolean; timeAttack?: boolean; saveData?: SaveData; daily?: boolean; date?: string; dailySaveData?: DailySaveData }): void {
    this.daily = data.daily ?? (data.dailySaveData !== undefined);
    this.dailyDate = data.date ?? data.dailySaveData?.date ?? '';
    this.level = data.saveData?.level ?? data.level ?? 1;
    this.replay = data.replay ?? false;
    this.timeAttack = data.timeAttack ?? false;
  }

  preload(): void {
    const tiles = [
      'moss-wetland', 'moss-forest', 'moss-alpine', 'moss-coral', 'moss-savanna',
      'fern-wetland', 'fern-forest', 'fern-alpine', 'fern-coral', 'fern-savanna',
      'flower-wetland', 'flower-forest', 'flower-alpine', 'flower-coral', 'flower-savanna',
      'mushroom-wetland', 'mushroom-forest', 'mushroom-alpine', 'mushroom-coral', 'mushroom-savanna',
      'shell-wetland', 'shell-forest', 'shell-alpine', 'shell-coral', 'shell-savanna',
      'vine-wetland', 'vine-forest', 'vine-alpine', 'vine-coral', 'vine-savanna',
      'sacred-spring', 'seed', 'wave',
      'ground-degraded', 'ground-wetland', 'ground-forest', 'ground-alpine', 'ground-coral', 'ground-meadow',
    ];
    for (const key of tiles) {
      this.load.image(key, `assets/tiles/${key}.png`);
    }
    const decorKeys = getBiomeDecorKeys(this.level);
    for (const key of decorKeys) {
      this.load.image(key, `assets/tiles/${key}.png`);
    }
    this.load.image('icon-map', 'assets/icons/map.png');
    this.load.image('icon-gear', 'assets/icons/gear.png');
    this.load.image('icon-question', 'assets/icons/question.png');
    this.load.image('volcano', 'assets/icons/volcano.png');
    this.load.image('spirit-dryad', 'assets/icons/spirit-dryad.png');
    this.load.image('spirit-shade', 'assets/icons/spirit-shade.png');
    this.load.image('spirit-luna', 'assets/icons/spirit-luna.png');
    this.load.image('spirit-lumina', 'assets/icons/spirit-lumina.png');
  }

  create(data: { level?: number; saveData?: SaveData; dailySaveData?: DailySaveData }): void {
    pushSceneState();
    const save = data.saveData;
    const dailySave = data.dailySaveData;

    let gridSize: number;
    let shapeId: ShapeId;
    let fromSave = false;

    if (this.daily && dailySave) {
      const puzzle = generateDailyPuzzle(this.dailyDate);
      gridSize = dailySave.gridSize;
      shapeId = dailySave.shapeId;
      this.grid = new Grid(gridSize, shapeId, dailySave.cells);
      const rng = createDailyRng(this.dailyDate);
      this.tileQueue = new TileQueue(
        puzzle.colors,
        puzzle.symbols,
        { current: dailySave.current, next: dailySave.next, isFirstTile: dailySave.isFirstTile, rngCalls: dailySave.rngCalls },
        rng,
      );
      this.scoring = new Scoring(this.level, { score: dailySave.score, streak: dailySave.streak });
      this.droughtGauge = new DroughtGauge(dailySave.heat, false, this.level);
      this.streaks = new Streaks({
        harmony: dailySave.harmony,
        patience: dailySave.patience,
        rhythm: dailySave.rhythm,
        shields: dailySave.shields,
      });
      fromSave = true;
    } else if (this.daily) {
      const puzzle = generateDailyPuzzle(this.dailyDate);
      gridSize = 7;
      shapeId = puzzle.shapeId;
      const cells = buildDailyCells(puzzle);
      this.grid = new Grid(gridSize, shapeId, cells);
      const rng = createDailyRng(this.dailyDate);
      this.tileQueue = new TileQueue(puzzle.colors, puzzle.symbols, undefined, rng);
      this.scoring = new Scoring(this.level);
      this.droughtGauge = new DroughtGauge(0, false, this.level);
      this.streaks = new Streaks();
      fromSave = false;
    } else if (save) {
      const tier = getDifficultyTier(this.level);
      gridSize = save.gridSize;
      shapeId = save.shapeId ?? tier.shapeId ?? 'square';
      this.grid = new Grid(gridSize, shapeId, save.cells);
      this.tileQueue = new TileQueue(tier.colors, tier.symbols, {
        current: save.current,
        next: save.next,
        isFirstTile: save.isFirstTile,
      });
      this.scoring = new Scoring(this.level, { score: save.score, streak: save.streak });
      this.droughtGauge = new DroughtGauge(save.heat ?? 0, this.timeAttack, this.level);
      this.streaks = new Streaks({
        harmony: save.harmony ?? 0,
        patience: save.patience ?? 0,
        rhythm: save.rhythm ?? 0,
        shields: save.shields ?? 0,
      });
      fromSave = true;
    } else {
      const tier = getDifficultyTier(this.level);
      gridSize = tier.gridSize;
      shapeId = tier.shapeId;
      this.grid = new Grid(gridSize, shapeId);
      this.tileQueue = new TileQueue(tier.colors, tier.symbols);
      this.scoring = new Scoring(this.level);
      this.droughtGauge = new DroughtGauge(0, this.timeAttack, this.level);
      this.streaks = new Streaks();
    }

    this.isGameOver = false;
    this.timerStarted = false;
    this.elapsedMs = 0;
    if (!this.daily) {
      setHighestLevel(this.level);
      clearDailySave();
    }
    this.calculateGridLayout(gridSize);
    this.createGrid(gridSize);
    this.createPreviewUI();
    this.createHelpButton();
    this.createDiscardZone();
    this.createStreakUI();
    this.createLuminaUI();
    this.updatePreviewUI();
    this.updateLavaFill();
    this.updateStreakCounters();
    this.updateLuminaDisplay();
    this.checkUnplaceable();

    if (!fromSave) {
      this.persistState();
    }

    getMusicPlayer().start(this.level);
    this.events.on('wake', () => getMusicPlayer().start(this.level));
  }

  update(_time: number, delta: number): void {
    if (!this.timeAttack || !this.timerStarted || this.isGameOver) return;

    this.elapsedMs += delta;
    this.droughtGauge.addPassiveHeat(delta / 1000);
    this.updateLavaFill();

    if (this.droughtGauge.isFull()) {
      this.isGameOver = true;
      this.tweens.killTweensOf(this.currentPreview);
      this.currentPreview.setAlpha(0);
      this.saveBest();
      this.clearCurrentSave();
      this.playEruption();
    }
  }

  private calculateGridLayout(gridSize: number): void {
    const padding = GAME_CONFIG.gridPadding;
    const availableWidth = GAME_CONFIG.width - padding * 2;
    this.cellSize = Math.floor(availableWidth / gridSize);
    const gridPixelSize = this.cellSize * gridSize;
    this.gridOriginX = (GAME_CONFIG.width - gridPixelSize) / 2;
    this.gridOriginY = 200;
  }

  private createGrid(gridSize: number): void {
    this.gridContainer = this.add.container(0, 0);
    this.cellSprites = [];
    this.tileSprites = [];
    this.cellRestored = [];

    for (let row = 0; row < gridSize; row++) {
      this.cellSprites[row] = [];
      this.tileSprites[row] = [];
      this.cellRestored[row] = [];
      for (let col = 0; col < gridSize; col++) {
        const cell = this.grid.getCell(row, col)!;

        if (!cell.active) {
          this.cellSprites[row][col] = null as unknown as Phaser.GameObjects.Image;
          this.tileSprites[row][col] = null;
          this.cellRestored[row][col] = true;
          continue;
        }

        const x = this.gridOriginX + col * this.cellSize + this.cellSize / 2;
        const y = this.gridOriginY + row * this.cellSize + this.cellSize / 2;
        const isRestored = cell.restored;

        const textureKey = isRestored ? getRestoredGroundKey(this.level) : 'ground-degraded';
        const cellBg = this.add.image(x, y, textureKey);
        cellBg.setDisplaySize(this.cellSize - 2, this.cellSize - 2);
        cellBg.setInteractive();
        cellBg.on('pointerdown', () => this.onCellTap(row, col));
        this.gridContainer.add(cellBg);
        this.cellSprites[row][col] = cellBg;

        this.cellRestored[row][col] = isRestored;

        if (cell.tile) {
          const tileSprite = this.add.image(x, y, tileAssetKey(cell.tile));
          const displaySize = this.cellSize - 6;
          tileSprite.setDisplaySize(displaySize, displaySize);
          this.gridContainer.add(tileSprite);
          this.tileSprites[row][col] = tileSprite;
        } else {
          this.tileSprites[row][col] = null;
        }
      }
    }
  }

  private createPreviewUI(): void {
    const previewSize = GAME_CONFIG.tilePreviewSize;
    const centerX = GAME_CONFIG.width / 2;
    const previewY = 120;
    const spacing = 100;

    if (this.daily) {
      this.add.text(GAME_CONFIG.width / 2, 26, 'Daily Challenge', {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'sans-serif',
      }).setOrigin(0.5);

      this.add.text(GAME_CONFIG.width / 2, 46, this.dailyDate, {
        fontSize: '14px',
        color: '#f0d060',
        fontFamily: 'sans-serif',
      }).setOrigin(0.5);
    } else {
      this.add.text(GAME_CONFIG.width / 2, 26, `Level ${this.level}`, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'sans-serif',
      }).setOrigin(0.5);

      this.add.text(GAME_CONFIG.width / 2, 46, getBiomeName(this.level), {
        fontSize: '14px',
        color: '#a8e6cf',
        fontFamily: 'sans-serif',
      }).setOrigin(0.5);
    }

    this.add.text(centerX - spacing, previewY - 30, 'CURRENT', {
      fontSize: '14px',
      color: '#a8e6cf',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.currentPreview = this.add.image(centerX - spacing, previewY + 10, 'sacred-spring');
    this.currentPreview.setDisplaySize(previewSize, previewSize);

    this.add.text(centerX + spacing, previewY - 30, 'NEXT', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);

    this.nextPreview = this.add.image(centerX + spacing, previewY + 10, 'sacred-spring');
    this.nextPreview.setDisplaySize(previewSize, previewSize);

    this.scoreText = this.add.text(GAME_CONFIG.width / 2, 180, `Score: ${this.scoring.getScore()}`, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
  }

  private createHelpButton(): void {
    const helpBg = this.add.rectangle(GAME_CONFIG.width - 28, 30, 36, 30, 0x333355);
    this.add.image(GAME_CONFIG.width - 28, 30, 'icon-question').setDisplaySize(22, 22);
    helpBg.setInteractive();

    helpBg.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.scene.sleep();
      this.scene.launch('HowToPlayScene', { returnTo: 'GameScene' });
    });

    const gearBg = this.add.rectangle(GAME_CONFIG.width - 72, 30, 36, 30, 0x333355);
    this.add.image(GAME_CONFIG.width - 72, 30, 'icon-gear').setDisplaySize(22, 22);
    gearBg.setInteractive();

    gearBg.on('pointerdown', () => {
      getSoundSystem().playBlip();
      this.scene.sleep();
      this.scene.launch('SettingsScene', { returnTo: 'GameScene' });
    });

    const mapBg = this.add.rectangle(GAME_CONFIG.width - 116, 30, 36, 30, 0x333355);
    this.add.image(GAME_CONFIG.width - 116, 30, 'icon-map').setDisplaySize(22, 22);
    mapBg.setInteractive();

    mapBg.on('pointerdown', () => {
      getSoundSystem().playBlip();
      getMusicPlayer().startMenu();
      this.scene.sleep();
      this.scene.launch('WorldMapScene', { returnTo: 'GameScene' });
    });
  }

  private updateLavaFill(): void {
    const maxHeight = 55;
    const lavaWidth = 34;
    const radius = 6;
    const percent = this.droughtGauge.getPercent();
    const fillHeight = maxHeight * percent;

    this.lavaFill.clear();
    if (fillHeight > 3) {
      this.lavaFill.fillStyle(0xff4400);
      this.lavaFill.fillRoundedRect(
        -lavaWidth / 2, 17 - fillHeight,
        lavaWidth, fillHeight,
        { tl: radius, tr: radius, bl: 0, br: 0 },
      );
    }

    this.updateVolcanoGlow(percent);
    getMusicPlayer().setHeat(percent);
  }

  private updateVolcanoGlow(percent: number): void {
    if (this.glowTween) {
      this.glowTween.stop();
      this.glowTween = null;
    }

    this.volcanoGlow.clear();

    if (percent <= 0) {
      this.volcanoGlow.setAlpha(0);
      return;
    }

    const layers = 8;
    const baseW = 100 + percent * 20;
    const baseH = 90 + percent * 20;
    for (let i = layers - 1; i >= 0; i--) {
      const t = i / (layers - 1);
      const alpha = 0.5 * (1 - t);
      this.volcanoGlow.fillStyle(0xff4400, alpha);
      this.volcanoGlow.fillEllipse(0, -10, baseW * (0.4 + 0.6 * t), baseH * (0.4 + 0.6 * t));
    }

    const maxAlpha = percent * 0.7;
    const speed = 900 - percent * 500;

    this.glowTween = this.tweens.add({
      targets: this.volcanoGlow,
      alpha: { from: maxAlpha * 0.3, to: maxAlpha },
      duration: speed,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    });
  }

  private checkUnplaceable(): void {
    const current = this.tileQueue.getCurrent();
    if (!this.grid.hasValidMove(current)) {
      if (!this.unplaceableTween) {
        this.unplaceableTween = this.tweens.add({
          targets: this.currentPreview,
          alpha: 0.3,
          duration: 400,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.inOut',
          onUpdate: () => {
            this.currentPreview.setTint(0xff4444);
          },
        });
      }
    } else {
      if (this.unplaceableTween) {
        this.unplaceableTween.stop();
        this.unplaceableTween = null;
        this.currentPreview.clearTint();
        this.currentPreview.setAlpha(1);
      }
    }
  }

  private createDiscardZone(): void {
    const centerX = GAME_CONFIG.width / 2;
    const gridBottom = this.gridOriginY + this.grid.size * this.cellSize;
    const discardY = gridBottom + 70;

    this.discardZone = this.add.container(centerX, discardY);

    this.volcanoGlow = this.add.graphics();
    this.volcanoGlow.setAlpha(0);
    this.discardZone.add(this.volcanoGlow);

    this.lavaFill = this.add.graphics();
    this.discardZone.add(this.lavaFill);

    this.volcanoIcon = this.add.image(0, -10, 'volcano');
    this.volcanoIcon.setDisplaySize(80, 80);
    this.discardZone.add(this.volcanoIcon);

    const label = this.add.text(0, 38, 'Discard', {
      fontSize: '14px',
      color: '#ff6b6b',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.discardZone.add(label);

    const hitArea = this.add.rectangle(0, 10, 100, 100, 0x000000, 0);
    hitArea.setInteractive();
    hitArea.on('pointerdown', () => this.onDiscard());
    this.discardZone.add(hitArea);
  }

  private onDiscard(): void {
    if (this.isGameOver) return;
    getSoundSystem().playDiscard();

    const { harmonyAtBreak } = this.streaks.onDiscard();
    if (harmonyAtBreak >= HARMONY_CONFIG.tiers[0].threshold) {
      this.playStreakBreak('harmony', harmonyAtBreak);
    }
    this.updateStreakCounters();

    const shielded = this.streaks.consumeShield();
    if (shielded) {
      this.playShieldAbsorb();
      this.updateLuminaDisplay();
    } else {
      this.droughtGauge.addDiscard();
      this.updateLavaFill();

      if (this.droughtGauge.isFull()) {
        this.isGameOver = true;
        this.tweens.killTweensOf(this.currentPreview);
        this.currentPreview.setAlpha(0);
        this.saveBest();
        this.clearCurrentSave();
        this.playEruption();
        return;
      }
    }

    this.persistState();

    this.tweens.add({
      targets: this.discardZone,
      scaleX: 0.85,
      scaleY: 0.85,
      duration: 80,
      yoyo: true,
      ease: 'Sine.inOut',
    });

    this.tweens.add({
      targets: this.currentPreview,
      alpha: 0,
      y: this.currentPreview.y + 20,
      duration: 150,
      ease: 'Sine.easeIn',
      onComplete: () => {
        this.tileQueue.advance();
        this.updatePreviewUI();
        this.persistState();
        this.currentPreview.setAlpha(1);
        this.currentPreview.y -= 20;
        this.checkUnplaceable();
      },
    });
  }

  private updatePreviewUI(): void {
    const current = this.tileQueue.getCurrent();
    const next = this.tileQueue.getNext();

    this.currentPreview.setTexture(tileAssetKey(current));
    this.currentPreview.setDisplaySize(
      GAME_CONFIG.tilePreviewSize,
      GAME_CONFIG.tilePreviewSize,
    );

    this.nextPreview.setTexture(tileAssetKey(next));
    this.nextPreview.setDisplaySize(
      GAME_CONFIG.tilePreviewSize,
      GAME_CONFIG.tilePreviewSize,
    );
  }

  private updateScoreDisplay(): void {
    this.scoreText.setText(`Score: ${this.scoring.getScore()}`);
  }

  private persistState(): void {
    const queueState = this.tileQueue.getState();
    const scoringState = this.scoring.getState();
    const streakState = this.streaks.getState();

    if (this.daily) {
      saveDailyGame({
        date: this.dailyDate,
        gridSize: this.grid.size,
        shapeId: this.grid.shapeId,
        cells: this.grid.getCells(),
        current: queueState.current,
        next: queueState.next,
        isFirstTile: queueState.isFirstTile,
        rngCalls: queueState.rngCalls ?? 0,
        score: scoringState.score,
        streak: scoringState.streak,
        heat: this.droughtGauge.getHeat(),
        harmony: streakState.harmony,
        patience: streakState.patience,
        rhythm: streakState.rhythm,
        shields: streakState.shields,
        lastPlayed: Date.now(),
      });
    } else {
      saveGame({
        level: this.level,
        gridSize: this.grid.size,
        shapeId: this.grid.shapeId,
        cells: this.grid.getCells(),
        current: queueState.current,
        next: queueState.next,
        isFirstTile: queueState.isFirstTile,
        score: scoringState.score,
        streak: scoringState.streak,
        heat: this.droughtGauge.getHeat(),
        harmony: streakState.harmony,
        patience: streakState.patience,
        rhythm: streakState.rhythm,
        shields: streakState.shields,
        lastPlayed: Date.now(),
      });
    }
  }

  private clearCurrentSave(): void {
    if (this.daily) {
      clearDailySave();
    } else {
      clearSave();
    }
  }

  private saveBest(): void {
    if (this.daily) {
      saveDailyBestScore(this.dailyDate, this.scoring.getScore());
    } else {
      this.scoring.saveBestScore();
    }
  }

  private getBestScore(): number {
    if (this.daily) {
      return getDailyBestScore(this.dailyDate) ?? 0;
    }
    return this.scoring.getBestScore();
  }

  private startTimeAttackTimer(): void {
    if (this.timeAttack && !this.timerStarted) {
      this.timerStarted = true;
    }
  }

  private onCellTap(row: number, col: number): void {
    if (this.isGameOver) return;

    const cell = this.grid.getCell(row, col);
    if (!cell) return;

    const currentTile = this.tileQueue.getCurrent();

    if (isWaveTile(currentTile)) {
      if (!this.grid.isValidWaveTarget(row, col)) {
        this.showInvalidFeedback(row, col);
        return;
      }
      this.placeWave(row, col);
      return;
    }

    if (cell.tile !== null) return;

    if (!this.grid.isValidPlacement(row, col, currentTile)) {
      this.showInvalidFeedback(row, col);
      return;
    }

    if (isSeedTile(currentTile)) {
      this.placeSeed(row, col);
      return;
    }

    this.grid.placeTile(row, col, currentTile);
    this.startTimeAttackTimer();
    getSoundSystem().playPlace();
    this.scoring.addPlacement(this.streaks.getMultiplier());
    this.droughtGauge.coolOnPlacement();
    this.updateScoreDisplay();
    this.updateLavaFill();
    this.restoreCell(row, col);
    this.renderPlacedTile(row, col, currentTile);
    this.tileQueue.advance();
    this.updatePreviewUI();

    if (this.grid.isAllRestored()) {
      this.isGameOver = true;
      this.saveBest();
      this.clearCurrentSave();
      this.showLevelComplete();
      return;
    }

    const { rows, cols } = this.grid.getCompletedLines(row, col);
    const producedClear = rows.length > 0 || cols.length > 0;
    const tiersBefore = this.captureStreakTiers();
    const streakResult = this.streaks.onPlacement(producedClear);
    this.checkTierUp(tiersBefore);
    this.updateStreakCounters();

    if (streakResult.patienceBroke) {
      this.playStreakBreak('patience', streakResult.patienceAtBreak);
      this.awardPatienceReward(streakResult.patienceAtBreak);
    }
    if (streakResult.rhythmBroke) {
      this.playStreakBreak('rhythm', streakResult.rhythmAtBreak);
      this.awardRhythmReward(streakResult.rhythmAtBreak);
    }

    this.persistState();

    if (producedClear) {
      this.time.delayedCall(200, () => this.animateClear(rows, cols));
    } else {
      this.scoring.resetStreak();
      this.persistState();
      this.checkUnplaceable();
    }
  }

  private placeSeed(row: number, col: number): void {
    this.startTimeAttackTimer();
    getSoundSystem().playPlace();
    const cell = this.grid.getCell(row, col)!;
    cell.restored = true;
    this.restoreCell(row, col);

    const x = this.gridOriginX + col * this.cellSize + this.cellSize / 2;
    const y = this.gridOriginY + row * this.cellSize + this.cellSize / 2;
    const displaySize = this.cellSize - 6;
    const seedSprite = this.add.image(x, y, SEED_TILE);
    seedSprite.setDisplaySize(displaySize, displaySize);
    this.gridContainer.add(seedSprite);

    seedSprite.setScale(0);
    this.tweens.add({
      targets: seedSprite,
      scaleX: displaySize / seedSprite.width,
      scaleY: displaySize / seedSprite.height,
      duration: 150,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: seedSprite,
          alpha: 0,
          scaleX: seedSprite.scaleX * 1.5,
          scaleY: seedSprite.scaleY * 1.5,
          duration: 300,
          ease: 'Sine.easeOut',
          onComplete: () => seedSprite.destroy(),
        });
      },
    });

    this.tileQueue.advance();
    this.updatePreviewUI();

    const seedTiersBefore = this.captureStreakTiers();
    const seedStreakResult = this.streaks.onPlacement(false);
    this.checkTierUp(seedTiersBefore);
    this.updateStreakCounters();
    if (seedStreakResult.rhythmBroke) {
      this.playStreakBreak('rhythm', seedStreakResult.rhythmAtBreak);
      this.awardRhythmReward(seedStreakResult.rhythmAtBreak);
    }

    this.persistState();

    if (this.grid.isAllRestored()) {
      this.isGameOver = true;
      this.saveBest();
      this.clearCurrentSave();
      this.showLevelComplete();
      return;
    }

    this.checkUnplaceable();
  }

  private placeWave(row: number, col: number): void {
    this.startTimeAttackTimer();
    getSoundSystem().playPlace();
    const cell = this.grid.getCell(row, col)!;
    cell.tile = null;

    const existingSprite = this.tileSprites[row][col];
    if (existingSprite) {
      this.tweens.add({
        targets: existingSprite,
        alpha: 0,
        scaleX: existingSprite.scaleX * 1.3,
        scaleY: existingSprite.scaleY * 1.3,
        duration: 300,
        ease: 'Sine.easeOut',
        onComplete: () => existingSprite.destroy(),
      });
      this.tileSprites[row][col] = null;
    }

    const x = this.gridOriginX + col * this.cellSize + this.cellSize / 2;
    const y = this.gridOriginY + row * this.cellSize + this.cellSize / 2;
    const displaySize = this.cellSize - 6;
    const waveSprite = this.add.image(x, y, WAVE_TILE);
    waveSprite.setDisplaySize(displaySize, displaySize);
    this.gridContainer.add(waveSprite);

    waveSprite.setAlpha(0);
    this.tweens.add({
      targets: waveSprite,
      alpha: 1,
      duration: 150,
      ease: 'Sine.easeIn',
      onComplete: () => {
        this.tweens.add({
          targets: waveSprite,
          alpha: 0,
          angle: 360,
          duration: 400,
          ease: 'Sine.easeOut',
          onComplete: () => waveSprite.destroy(),
        });
      },
    });

    this.tileQueue.advance();
    this.updatePreviewUI();

    const waveTiersBefore = this.captureStreakTiers();
    const waveStreakResult = this.streaks.onPlacement(false);
    this.checkTierUp(waveTiersBefore);
    this.updateStreakCounters();
    if (waveStreakResult.rhythmBroke) {
      this.playStreakBreak('rhythm', waveStreakResult.rhythmAtBreak);
      this.awardRhythmReward(waveStreakResult.rhythmAtBreak);
    }

    this.persistState();
    this.checkUnplaceable();
  }

  private renderPlacedTile(row: number, col: number, tile: PlaceableTile): void {
    const x = this.gridOriginX + col * this.cellSize + this.cellSize / 2;
    const y = this.gridOriginY + row * this.cellSize + this.cellSize / 2;

    const tileSprite = this.add.image(x, y, tileAssetKey(tile));
    const displaySize = this.cellSize - 6;
    tileSprite.setDisplaySize(displaySize, displaySize);
    this.gridContainer.add(tileSprite);
    this.tileSprites[row][col] = tileSprite;

    tileSprite.setScale(0);
    this.tweens.add({
      targets: tileSprite,
      scaleX: displaySize / tileSprite.width,
      scaleY: displaySize / tileSprite.height,
      duration: 150,
      ease: 'Back.easeOut',
    });
  }

  private restoreCell(row: number, col: number): void {
    if (this.cellRestored[row][col]) return;

    this.cellRestored[row][col] = true;
    const cellBg = this.cellSprites[row][col];

    cellBg.setTexture(getRestoredGroundKey(this.level));
    cellBg.setDisplaySize(this.cellSize - 2, this.cellSize - 2);

    cellBg.setAlpha(0);
    this.tweens.add({
      targets: cellBg,
      alpha: 1,
      duration: 150,
      ease: 'Sine.easeOut',
    });
  }

  private animateClear(rows: number[], cols: number[]): void {
    getSoundSystem().playClear();
    const isCrossClear = rows.length > 0 && cols.length > 0;
    const diverse = this.grid.isLineDiverse(rows, cols);
    const monoSymbol = !diverse && this.grid.isLineMonoSymbol(rows, cols);
    const monoColor = !diverse && !monoSymbol && this.grid.isLineMonoColor(rows, cols);

    const lineCount = rows.length + cols.length;
    this.scoring.addClear(lineCount, this.streaks.getMultiplier());
    if (this.timeAttack) {
      this.droughtGauge.coolOnClear(lineCount);
    } else {
      this.droughtGauge.reset();
    }
    this.updateScoreDisplay();
    this.updateLavaFill();

    const positions = new Set<string>();
    for (const r of rows) {
      for (let c = 0; c < this.grid.size; c++) {
        if (this.grid.isCellActive(r, c)) positions.add(`${r},${c}`);
      }
    }
    for (const c of cols) {
      for (let r = 0; r < this.grid.size; r++) {
        if (this.grid.isCellActive(r, c)) positions.add(`${r},${c}`);
      }
    }

    const cellsToClear: Array<{ row: number; col: number }> = [];
    for (const key of positions) {
      const [r, c] = key.split(',').map(Number);
      cellsToClear.push({ row: r, col: c });
    }

    this.grid.clearCells(cellsToClear);
    this.playClearParticles(cellsToClear, isCrossClear);

    let completed = 0;
    const total = cellsToClear.length;

    for (const { row, col } of cellsToClear) {
      const sprite = this.tileSprites[row][col];
      if (!sprite) {
        completed++;
        continue;
      }

      this.tweens.add({
        targets: sprite,
        alpha: 0,
        scaleX: sprite.scaleX * 1.3,
        scaleY: sprite.scaleY * 1.3,
        duration: 400,
        ease: 'Sine.easeOut',
        onComplete: () => {
          sprite.destroy();
          this.tileSprites[row][col] = null;
          completed++;
          if (completed === total) {
            const hasTargets = !this.grid.isEmpty();
            if (diverse) {
              this.tileQueue.replaceCurrent(SACRED_SPRING);
              this.updatePreviewUI();
              this.playSpiritGlow('sacred-spring');
            } else if (monoSymbol && hasTargets) {
              this.tileQueue.replaceCurrent(WAVE_TILE);
              this.updatePreviewUI();
              this.playSpiritGlow('wave');
            } else if (monoColor) {
              this.tileQueue.replaceCurrent(SEED_TILE);
              this.updatePreviewUI();
              this.playSpiritGlow('seed');
            } else {
              this.updatePreviewUI();
            }
            this.persistState();
            this.checkUnplaceable();
          }
        },
      });
    }

    if (total === 0) {
      this.persistState();
    }
  }

  private showInvalidFeedback(row: number, col: number): void {
    getSoundSystem().playInvalid();
    const cellBg = this.cellSprites[row][col];
    const correctX = this.gridOriginX + col * this.cellSize + this.cellSize / 2;

    this.tweens.killTweensOf(cellBg);
    cellBg.x = correctX;

    this.tweens.add({
      targets: cellBg,
      x: correctX + 4,
      duration: 50,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.inOut',
      onComplete: () => {
        cellBg.x = correctX;
      },
    });

    cellBg.setTint(0xff4444);
    this.time.delayedCall(300, () => {
      cellBg.clearTint();
    });
  }

  private playEruption(): void {
    getSoundSystem().playEruption();
    const volcanoWorldX = this.discardZone.x;
    const volcanoWorldY = this.discardZone.y - 30;

    const colors = [0xff2200, 0xff4400, 0xff6600, 0xff8800, 0xffaa00, 0xcc1100];
    const particleCount = 30;

    for (let i = 0; i < particleCount; i++) {
      const size = Phaser.Math.Between(6, 16);
      const color = colors[Math.floor(Math.random() * colors.length)];
      const particle = this.add.circle(
        volcanoWorldX + Phaser.Math.Between(-10, 10),
        volcanoWorldY,
        size,
        color,
      );

      const angle = Phaser.Math.Between(-120, -60);
      const speed = Phaser.Math.Between(150, 350);
      const vx = Math.cos(Phaser.Math.DegToRad(angle)) * speed;
      const vy = Math.sin(Phaser.Math.DegToRad(angle)) * speed;

      this.tweens.add({
        targets: particle,
        x: particle.x + vx * 0.8,
        y: particle.y + vy * 0.8 + 80,
        alpha: 0,
        scale: { from: 1, to: 0.3 },
        duration: Phaser.Math.Between(600, 1000),
        delay: Phaser.Math.Between(0, 200),
        ease: 'Sine.easeOut',
        onComplete: () => particle.destroy(),
      });
    }

    this.cameras.main.shake(300, 0.01);

    this.time.delayedCall(1200, () => {
      getMusicPlayer().stop();
      this.scene.start('GameOverScene', {
        level: this.level,
        score: this.scoring.getScore(),
        bestScore: this.getBestScore(),
        timeAttack: this.timeAttack,
        elapsedMs: this.elapsedMs,
        daily: this.daily,
        dailyDate: this.dailyDate,
      });
    });
  }

  private showLevelComplete(): void {
    getSoundSystem().playLevelComplete();
    const gridSize = this.grid.size;
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const sprite = this.tileSprites[row][col];
        if (sprite) {
          this.tweens.add({
            targets: sprite,
            alpha: 0,
            duration: 600,
            ease: 'Sine.easeOut',
            onComplete: () => sprite.destroy(),
          });
        }
      }
    }

    this.time.delayedCall(700, () => {
      this.showCelebrationTiles();
    });

    this.time.delayedCall(3200, () => {
      getMusicPlayer().stop();
      this.scene.start('LevelCompleteScene', {
        level: this.level,
        score: this.scoring.getScore(),
        bestScore: this.getBestScore(),
        replay: this.replay,
        timeAttack: this.timeAttack,
        elapsedMs: this.elapsedMs,
        daily: this.daily,
        dailyDate: this.dailyDate,
      });
    });
  }

  private showCelebrationTiles(): void {
    const gridSize = this.grid.size;
    const decorKeys = getBiomeDecorKeys(this.level);
    const activeCells: { row: number; col: number }[] = [];

    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const cell = this.grid.getCell(row, col);
        if (cell?.active) {
          activeCells.push({ row, col });
        }
      }
    }

    const decorCount = Math.round(activeCells.length * 0.35);
    const shuffled = activeCells.sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, decorCount);
    const displaySize = this.cellSize - 6;

    for (const { row, col } of selected) {
      const x = this.gridOriginX + col * this.cellSize + this.cellSize / 2;
      const y = this.gridOriginY + row * this.cellSize + this.cellSize / 2;
      const key = decorKeys[Math.floor(Math.random() * decorKeys.length)];

      const sprite = this.add.image(x, y, key);
      sprite.setDisplaySize(displaySize, displaySize);
      sprite.setAlpha(0);
      this.gridContainer.add(sprite);

      this.tweens.add({
        targets: sprite,
        alpha: 1,
        duration: 500,
        ease: 'Sine.easeIn',
      });
    }
  }

  private createStreakUI(): void {
    const gridBottom = this.gridOriginY + this.grid.size * this.cellSize;
    const discardBottom = gridBottom + 70 + 60;
    const streakY = discardBottom + 40;
    const streakSpacing = 80;

    const streakDefs = [
      { key: 'harmony', icon: 'spirit-dryad', color: 0x6bcb77 },
      { key: 'patience', icon: 'spirit-shade', color: 0x9b59b6 },
      { key: 'rhythm', icon: 'spirit-luna', color: 0x7ec8e3 },
    ];

    const startX = GAME_CONFIG.width / 2 - (streakDefs.length - 1) * streakSpacing / 2;

    for (let i = 0; i < streakDefs.length; i++) {
      const def = streakDefs[i];
      const x = startX + i * streakSpacing;
      const container = this.add.container(x, streakY);
      container.setAlpha(0);

      const glow = this.add.graphics();
      container.add(glow);
      this.streakGlows.set(def.key, glow);

      const icon = this.add.image(0, 0, def.icon);
      icon.setDisplaySize(64, 64);
      container.add(icon);

      const text = this.add.text(0, 40, '0', {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'sans-serif',
      }).setOrigin(0.5);
      container.add(text);
      this.streakTexts.set(def.key, text);

      this.streakContainers.set(def.key, container);
    }

    const harmonyX = startX;
    this.multiplierText = this.add.text(harmonyX, streakY - 40, '', {
      fontSize: '14px',
      color: '#a8e6cf',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.multiplierText.setAlpha(0);
  }

  private updateStreakCounters(): void {
    const streakData = [
      { key: 'harmony', count: this.streaks.getHarmony(), config: HARMONY_CONFIG, color: 0x6bcb77 },
      { key: 'patience', count: this.streaks.getPatience(), config: PATIENCE_CONFIG, color: 0x9b59b6 },
      { key: 'rhythm', count: this.streaks.getRhythm(), config: RHYTHM_CONFIG, color: 0x7ec8e3 },
    ];

    for (const { key, count, config, color } of streakData) {
      const container = this.streakContainers.get(key)!;
      const text = this.streakTexts.get(key)!;
      const glow = this.streakGlows.get(key)!;
      const tier = getCurrentTier(count, config);

      if (tier > 0) {
        container.setAlpha(1);
        text.setText(String(count));
        this.drawStreakGlow(glow, tier, color);
      } else {
        container.setAlpha(0);
      }
    }

    const multiplier = this.streaks.getMultiplier();
    if (this.multiplierText) {
      if (multiplier > 1) {
        this.multiplierText.setText(`×${multiplier}`);
        this.multiplierText.setAlpha(1);
      } else {
        this.multiplierText.setAlpha(0);
      }
    }
  }

  private drawStreakGlow(glow: Phaser.GameObjects.Graphics, tier: number, color: number): void {
    glow.clear();
    const layers = 3 + tier;
    const baseSize = 30 + tier * 8;
    for (let i = layers - 1; i >= 0; i--) {
      const t = i / (layers - 1);
      const alpha = (0.15 + tier * 0.1) * (1 - t);
      glow.fillStyle(color, alpha);
      glow.fillEllipse(0, 0, baseSize * (0.4 + 0.6 * t), baseSize * (0.4 + 0.6 * t));
    }
  }

  private captureStreakTiers(): { harmony: number; patience: number; rhythm: number } {
    return {
      harmony: this.streaks.getHarmonyTier(),
      patience: this.streaks.getPatienceTier(),
      rhythm: this.streaks.getRhythmTier(),
    };
  }

  private checkTierUp(before: { harmony: number; patience: number; rhythm: number }): void {
    const after = this.captureStreakTiers();
    if (after.harmony > before.harmony || after.patience > before.patience || after.rhythm > before.rhythm) {
      getSoundSystem().playStreakTierUp();
    }
  }

  private playStreakBreak(type: string, count: number): void {
    getSoundSystem().playStreakBreak();
    const container = this.streakContainers.get(type);
    if (!container) return;

    const colorMap: Record<string, number> = {
      harmony: 0x6bcb77,
      patience: 0x9b59b6,
      rhythm: 0x7ec8e3,
    };
    const color = colorMap[type] ?? 0xffffff;

    const x = container.x;
    const y = container.y;
    const particleCount = 8 + Math.min(count, 20);

    for (let i = 0; i < particleCount; i++) {
      const size = Phaser.Math.Between(2, 5);
      const particle = this.add.circle(x, y, size, color);
      const angle = (i / particleCount) * 360;
      const dist = Phaser.Math.Between(20, 40);
      const vx = Math.cos(Phaser.Math.DegToRad(angle)) * dist;
      const vy = Math.sin(Phaser.Math.DegToRad(angle)) * dist;

      this.tweens.add({
        targets: particle,
        x: x + vx,
        y: y + vy,
        alpha: 0,
        scale: { from: 1, to: 0.2 },
        duration: Phaser.Math.Between(400, 600),
        ease: 'Sine.easeOut',
        onComplete: () => particle.destroy(),
      });
    }

    this.tweens.add({
      targets: container,
      scaleX: 1.3,
      scaleY: 1.3,
      duration: 150,
      yoyo: true,
      ease: 'Back.easeOut',
    });
  }

  private createLuminaUI(): void {
    const gridBottom = this.gridOriginY + this.grid.size * this.cellSize;
    const discardY = gridBottom + 70;
    const luminaX = GAME_CONFIG.width / 2 - 90;
    const luminaY = discardY - 10;

    this.luminaContainer = this.add.container(luminaX, luminaY);
    this.luminaContainer.setAlpha(0);

    this.luminaGlow = this.add.graphics();
    this.luminaContainer.add(this.luminaGlow);

    const icon = this.add.image(0, 0, 'spirit-lumina');
    icon.setDisplaySize(48, 48);
    this.luminaContainer.add(icon);
  }

  private updateLuminaDisplay(): void {
    if (!this.luminaContainer || !this.luminaGlow) return;

    const shields = this.streaks.getShields();
    if (shields > 0) {
      this.luminaContainer.setAlpha(1);
      this.luminaGlow.clear();
      const capped = Math.min(shields, 4);
      const layers = 3 + capped * 2;
      const baseSize = 40 + capped * 20;
      for (let i = layers - 1; i >= 0; i--) {
        const t = i / (layers - 1);
        const alpha = (0.2 + capped * 0.15) * (1 - t);
        this.luminaGlow.fillStyle(0xfff0c0, alpha);
        this.luminaGlow.fillEllipse(0, 0, baseSize * (0.3 + 0.7 * t), baseSize * (0.3 + 0.7 * t));
      }
    } else {
      this.luminaContainer.setAlpha(0);
    }
  }

  private playShieldAbsorb(): void {
    getSoundSystem().playShieldConsume();
    if (!this.luminaContainer) return;

    const x = this.luminaContainer.x;
    const y = this.luminaContainer.y;

    const flash = this.add.graphics();
    flash.setPosition(x, y);
    flash.fillStyle(0xfff0c0, 0.6);
    flash.fillEllipse(0, 0, 60, 60);
    flash.setAlpha(0);

    this.tweens.add({
      targets: flash,
      alpha: { from: 1, to: 0 },
      scaleX: { from: 0.5, to: 1.5 },
      scaleY: { from: 0.5, to: 1.5 },
      duration: 300,
      ease: 'Sine.easeOut',
      onComplete: () => flash.destroy(),
    });
  }

  private awardPatienceReward(patienceCount: number): void {
    const tier = getCurrentTier(patienceCount, PATIENCE_CONFIG);
    if (tier === 0) return;

    const scoreBonus = patienceCount * 10;
    this.scoring.addBonus(scoreBonus);
    this.updateScoreDisplay();
    this.playScoreBurst(scoreBonus, 'patience');

    if (tier >= 2) {
      const shieldCount = tier >= 3 ? 2 : 1;
      this.streaks.addShields(shieldCount);
      this.updateLuminaDisplay();
      getSoundSystem().playShieldEarn();
    }

    if (tier >= 3) {
      this.tileQueue.replaceCurrent(SEED_TILE);
      this.updatePreviewUI();
      this.playSpiritGlow('seed');
    }
  }

  private awardRhythmReward(rhythmCount: number): void {
    const tier = getCurrentTier(rhythmCount, RHYTHM_CONFIG);
    if (tier === 0) return;

    if (tier === 1) {
      const tile = Math.random() < 0.5 ? SEED_TILE : WAVE_TILE;
      this.tileQueue.replaceCurrent(tile);
      this.updatePreviewUI();
      this.playSpiritGlow(tile === SEED_TILE ? 'seed' : 'wave');
    } else {
      this.tileQueue.replaceCurrent(SACRED_SPRING);
      this.updatePreviewUI();
      this.playSpiritGlow('sacred-spring');
    }
  }

  private playScoreBurst(amount: number, type: 'patience' | 'rhythm'): void {
    const container = this.streakContainers.get(type);
    if (!container) return;

    const colorMap = { patience: '#9b59b6', rhythm: '#7ec8e3' };
    const text = this.add.text(container.x, container.y - 30, `+${amount}`, {
      fontSize: '18px',
      color: colorMap[type],
      fontFamily: 'sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.tweens.add({
      targets: text,
      y: text.y - 40,
      alpha: 0,
      duration: 800,
      ease: 'Sine.easeOut',
      onComplete: () => text.destroy(),
    });
  }

  private playClearParticles(cells: Array<{ row: number; col: number }>, enhanced: boolean): void {
    const colors = getBiomeParticleColors(this.level);
    const count = enhanced ? 18 : 10;
    const distance = enhanced ? 60 : 35;
    const duration = enhanced ? 900 : 600;

    for (const { row, col } of cells) {
      const x = this.gridOriginX + col * this.cellSize + this.cellSize / 2;
      const y = this.gridOriginY + row * this.cellSize + this.cellSize / 2;

      for (let i = 0; i < count; i++) {
        const size = Phaser.Math.Between(3, 7);
        const color = colors[Math.floor(Math.random() * colors.length)];
        const particle = this.add.circle(x, y, size, color);

        const angle = (i / count) * 360 + Phaser.Math.Between(-15, 15);
        const speed = Phaser.Math.Between(distance * 0.6, distance);
        const vx = Math.cos(Phaser.Math.DegToRad(angle)) * speed;
        const vy = Math.sin(Phaser.Math.DegToRad(angle)) * speed;

        this.tweens.add({
          targets: particle,
          x: x + vx,
          y: y + vy,
          alpha: 0,
          scale: { from: 1, to: 0.2 },
          duration: Phaser.Math.Between(duration * 0.7, duration),
          delay: Phaser.Math.Between(0, 100),
          ease: 'Sine.easeOut',
          onComplete: () => particle.destroy(),
        });
      }
    }
  }

  private playSpiritGlow(powerType: 'seed' | 'sacred-spring' | 'wave'): void {
    getSoundSystem().playSpiritPower();
    const colorMap = {
      'seed': 0x6bcb77,
      'sacred-spring': 0xf0d060,
      'wave': 0x4ecde0,
    };
    const glowColor = colorMap[powerType];

    const previewX = this.currentPreview.x;
    const previewY = this.currentPreview.y;

    const glow = this.add.graphics();
    glow.setPosition(previewX, previewY);

    const layers = 6;
    const baseSize = 90;
    for (let i = layers - 1; i >= 0; i--) {
      const t = i / (layers - 1);
      const alpha = 0.4 * (1 - t);
      glow.fillStyle(glowColor, alpha);
      glow.fillEllipse(0, 0, baseSize * (0.3 + 0.7 * t), baseSize * (0.3 + 0.7 * t));
    }

    glow.setAlpha(0);
    glow.setScale(0.3);

    this.tweens.add({
      targets: glow,
      alpha: { from: 0, to: 1 },
      scaleX: 1,
      scaleY: 1,
      duration: 250,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: glow,
          alpha: 0,
          scaleX: 1.3,
          scaleY: 1.3,
          duration: 300,
          ease: 'Sine.easeIn',
          onComplete: () => glow.destroy(),
        });
      },
    });

    const baseScaleX = this.currentPreview.scaleX;
    const baseScaleY = this.currentPreview.scaleY;
    this.tweens.add({
      targets: this.currentPreview,
      scaleX: baseScaleX * 1.3,
      scaleY: baseScaleY * 1.3,
      duration: 150,
      ease: 'Back.easeOut',
      yoyo: true,
    });
  }
}
